/**
 * ModularStandDetailPage / ModularStandDetailBody
 * ──────────────────────────────────────────────
 * `ModularStandDetailBody` is the inner content (no page wrapper / no header).
 * It is rendered INSIDE the CustomerDashboard layout so that the site header
 * (logo, notifications, cart, language toggle, logout) stays visible.
 *
 * Bilingual routes (handled by CustomerDashboard which mounts this body):
 *   /musteri-admin/:slug/modular-stands/:standId       (EN)
 *   /musteri-admin/:slug/moduler-standlar/:standId     (TR)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../lib/apiClient';
import { Package, Ruler, Tag, ShoppingCart, Plus, Minus, ChevronLeft, Loader2, Search, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ModularStandDetailBody({ standId: propStandId }) {
  const params = useParams();
  const standId = propStandId || params.standId;
  const slug = params.slug;
  const { language } = useLanguage();
  const tr = language === 'tr';
  const navigate = useNavigate();

  const [stand, setStand] = useState(null);
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [idx, setIdx] = useState(0);
  const [extraQtys, setExtraQtys] = useState({});
  const [pkgQtys, setPkgQtys] = useState({}); // qty already included in the package (min floor)
  const [adding, setAdding] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [catFilter, setCatFilter] = useState('ALL');
  const [subCatFilter, setSubCatFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [allocated, setAllocated] = useState(false);

  const backPath = `/musteri-admin/${slug}/design`;
  const backLabel = tr ? 'Tüm Modüler Standlar' : 'All Modular Stands';

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('cop31_token');
        if (!token) { navigate('/giris'); return; }
        const headers = { Authorization: `Bearer ${token}` };

        const [stRes, exRes, lyRes] = await Promise.all([
          apiClient.get(`/customer/modular-stands`),
          apiClient.get(`/customer/organizer-products`),
          apiClient.get(`/customer/my-layout`).catch(() => ({ data: null })),
        ]);

        const layout = lyRes.data || null;
        setAllocated(!!(layout && layout.stand_no));

        const match = (stRes.data || []).find(s => s.id === standId);
        if (!match) {
          setNotFound(true);
        } else {
          setStand(match);
          // Pre-fill qtys from package contents (included in stand price)
          const prefill = {};
          for (const it of (match.package?.items || [])) {
            if (it.product_id) prefill[it.product_id] = Number(it.qty) || 0;
          }
          setPkgQtys(prefill);
          setExtraQtys({ ...prefill });
        }
        setExtras(exRes.data || []);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [standId, navigate]);

  const bump = (pid, delta, max) => {
    setExtraQtys(q => {
      const cur = Number(q[pid] || 0);
      const floor = Number(pkgQtys[pid] || 0); // can't go below what's in the package
      const next = Math.max(floor, Math.min(max ?? Number.MAX_SAFE_INTEGER, cur + delta));
      return { ...q, [pid]: next };
    });
  };

  const selectedExtrasCount = extras.reduce((acc, ex) => {
    const q = Number(extraQtys[ex.id] || 0);
    const floor = Number(pkgQtys[ex.id] || 0);
    return acc + (q - floor > 0 ? 1 : 0);
  }, 0);

  // Distinct categories/subcategories for filter pills
  const categories = useMemo(() => {
    const map = new Map();
    for (const ex of extras) {
      const cat = tr ? (ex.category_tr || '—') : (ex.category_en || '—');
      if (!map.has(cat)) map.set(cat, new Set());
      const sub = tr ? (ex.sub_category_tr || '') : (ex.sub_category_en || '');
      if (sub) map.get(cat).add(sub);
    }
    return [...map.entries()].map(([name, subs]) => ({ name, subs: [...subs] }));
  }, [extras, tr]);

  // Filtered extras list (by category + search)
  const filteredExtras = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return extras.filter(ex => {
      const cat = tr ? (ex.category_tr || '—') : (ex.category_en || '—');
      const sub = tr ? (ex.sub_category_tr || '') : (ex.sub_category_en || '');
      if (catFilter !== 'ALL' && cat !== catFilter) return false;
      if (subCatFilter !== 'ALL' && sub !== subCatFilter) return false;
      if (needle) {
        const nm = ((tr ? ex.name_tr : ex.name_en) || ex.name || '').toLowerCase();
        if (!nm.includes(needle)) return false;
      }
      return true;
    });
  }, [extras, catFilter, subCatFilter, search, tr]);

  // Summary lines (computed every render)
  const summaryLines = useMemo(() => {
    const lines = [];
    for (const ex of extras) {
      const q = Number(extraQtys[ex.id] || 0);
      const floor = Number(pkgQtys[ex.id] || 0);
      const extraOnly = q - floor;
      if (extraOnly <= 0) continue;
      const price = Number(ex.extra_price || 0);
      lines.push({
        id: ex.id,
        name: tr ? (ex.name_tr || ex.name) : (ex.name_en || ex.name),
        qty: extraOnly,
        price,
        lineTotal: price * extraOnly,
        currency: ex.currency || stand?.currency || 'EUR',
        thumb: ex.thumb_url,
      });
    }
    return lines;
  }, [extras, extraQtys, pkgQtys, stand, tr]);

  const extrasTotal = summaryLines.reduce((sum, ln) => sum + ln.lineTotal, 0);
  const grandTotal  = Number(stand?.price || 0) + extrasTotal;

  const handleAddToCart = async () => {
    if (!stand) return;
    setAdding(true);
    try {
      const token = localStorage.getItem('cop31_token');
      const headers = { Authorization: `Bearer ${token}` };
      const title = tr ? (stand.title_tr || stand.title) : (stand.title_en || stand.title);
      const lines = [];

      lines.push({
        product_id:         `modular-package-${stand.id}`,
        product_name:       `${title}${stand.area_m2 ? ` (${stand.area_m2} m²)` : ''}`,
        product_image:      stand.thumb_url || stand.cover_url || null,
        calculation_type:   'flat',
        unit_price:         Number(stand.price || 0),
        line_total:         Number(stand.price || 0),
        currency:           stand.currency || 'EUR',
        quantity:           1,
        supplier_tenant_id: 'organizer',
        notes:              tr ? `COP 31 Modüler Stand${stand.area_m2 ? ` · ${stand.area_m2} m²` : ''}` : `COP 31 Modular Stand${stand.area_m2 ? ` · ${stand.area_m2} m²` : ''}`,
      });

      for (const ex of extras) {
        const q = Number(extraQtys[ex.id] || 0);
        const floor = Number(pkgQtys[ex.id] || 0);
        const extraQ = q - floor; // amount beyond what the package includes
        if (extraQ <= 0) continue;
        const price = Number(ex.extra_price || 0);
        const exName = tr ? (ex.name_tr || ex.name) : (ex.name_en || ex.name);
        lines.push({
          product_id:         `organizer-extra-${ex.id}`,
          product_name:       `${exName} (${tr ? 'Ekstra' : 'Extra'})`,
          product_image:      ex.thumb_url || ex.cover_url || null,
          calculation_type:   'quantity_based',
          unit_price:         price,
          line_total:         price * extraQ,
          currency:           ex.currency || stand.currency || 'EUR',
          quantity:           extraQ,
          supplier_tenant_id: 'organizer',
          notes:              tr ? 'Modüler stand ekstrası' : 'Modular stand extra',
        });
      }

      await apiClient.post(`/customer/cart/add-lines`, { lines });
      toast.success(tr ? 'Sepete eklendi.' : 'Added to cart.');
      window.dispatchEvent(new CustomEvent('cop31:cart-refresh'));
      setShowSummary(false);
      navigate(backPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || (tr ? 'Sepete eklenemedi.' : 'Failed to add.'));
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-10 w-10 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (notFound || !stand) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
        <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500 mb-4">{tr ? 'Stand bulunamadı.' : 'Stand not found.'}</p>
        <button
          onClick={() => navigate(backPath)}
          className="text-indigo-600 hover:underline font-semibold text-sm"
        >← {backLabel}</button>
      </div>
    );
  }

  const title = tr ? (stand.title_tr || stand.title) : (stand.title_en || stand.title);
  const desc  = tr ? (stand.description_tr) : (stand.description_en);
  const pkg   = stand.package;
  const imgs  = stand.images?.length ? stand.images : (stand.cover_url ? [{ url: stand.cover_url }] : []);

  return (
    <div className="pb-24">
      {/* Breadcrumb */}
      <div className="mb-5 flex items-center gap-2">
        <button
          onClick={() => navigate(backPath)}
          data-testid="stand-detail-back-btn"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
        >
          <ChevronLeft className="h-4 w-4" />
          {backLabel}
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-sm font-medium text-gray-700 truncate">{title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left — Gallery */}
        <div>
          <div className="aspect-square bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-200 relative">
            {imgs[idx]?.url
              ? <img
                  src={imgs[idx].url}
                  alt={title}
                  className="w-full h-full object-contain"
                  data-testid="modular-stand-main-image"
                  onError={(e) => {
                    // Phase 86J — If the display version is missing from S3
                    // (half-orphan), fall back once to the thumbnail URL so
                    // the customer still sees the stand instead of a blank.
                    const fallback = imgs[idx]?.thumb_url;
                    if (fallback && e.target.src !== fallback && !e.target.dataset.fallbackTried) {
                      e.target.dataset.fallbackTried = '1';
                      e.target.src = fallback;
                    } else {
                      e.target.style.display = 'none';
                    }
                  }}
                />
              : <div className="w-full h-full flex items-center justify-center"><Package className="h-20 w-20 text-gray-300" /></div>}
            {stand.area_m2 && (
              <span className="absolute top-4 left-4 bg-indigo-600 text-white text-xs font-black px-3 py-1 rounded-full tracking-widest shadow">
                {stand.area_m2} m²
              </span>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
              {imgs.map((im, i) => (
                <button
                  key={im.id || i}
                  onClick={() => setIdx(i)}
                  className={`h-20 w-20 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all ${i === idx ? 'border-indigo-500' : 'border-gray-200'}`}
                  aria-label={`Image ${i + 1}`}
                >
                  <img src={im.thumb_url || im.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right — Info */}
        <div className="space-y-5">
          <div>
            <h1 className="text-3xl font-black text-gray-900 leading-tight">{title}</h1>
            {stand.category && (
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                <Tag className="h-3 w-3" />{stand.category}
              </span>
            )}
          </div>

          {desc && (
            <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{desc}</p>
          )}

          <div className="grid grid-cols-2 gap-3">
            {stand.area_m2 && (
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1"><Ruler className="h-3 w-3" /> {tr ? 'Alan' : 'Area'}</p>
                <p className="font-bold text-gray-900 text-lg">{stand.area_m2} m²</p>
              </div>
            )}
            {(stand.price || 0) > 0 && (
              <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                <p className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wider">{tr ? 'Stand Fiyatı' : 'Stand Price'}</p>
                <p className="font-black text-indigo-900 text-lg">
                  {Number(stand.price || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} {stand.currency}
                </p>
              </div>
            )}
          </div>

          {pkg ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-3">
                {tr ? `Paket İçeriği (${pkg.items?.length || 0})` : `Package Contents (${pkg.items?.length || 0})`}
              </h3>
              {(pkg.items || []).length === 0 ? (
                <p className="text-xs text-gray-400 italic">{tr ? 'Kalem eklenmedi' : 'No items listed'}</p>
              ) : (
                <ul className="space-y-2">
                  {pkg.items.map((it, i) => {
                    const itemName = tr ? (it.product?.name_tr || it.product?.name_en) : (it.product?.name_en || it.product?.name_tr);
                    return (
                      <li key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100" data-testid={`pkg-item-${i}`}>
                        {it.product?.thumb_url
                          ? <img src={it.product.thumb_url} alt="" className="h-10 w-10 rounded object-cover" loading="lazy" />
                          : <div className="h-10 w-10 rounded bg-gray-200" />}
                        <span className="flex-1 text-sm font-medium text-gray-800 truncate">{itemName}</span>
                        <span className="font-bold text-gray-900 text-sm">×{it.qty}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <p className="text-sm text-amber-800 font-semibold mb-1">
                {tr ? 'Bu stand için özel paket henüz hazırlanmadı.' : 'No custom package prepared for this stand yet.'}
              </p>
              <p className="text-xs text-amber-700">
                {tr
                  ? 'Standı mevcut fiyatıyla alabilir, aşağıdaki ekstra ürünlerden istediğinizi sepete ekleyebilirsiniz.'
                  : 'You can still book the stand at its current price and add any optional extras below to your cart.'}
              </p>
            </div>
          )}

          {extras.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-2 flex items-center gap-2">
                <Plus className="h-3.5 w-3.5 text-indigo-600" />
                {tr ? `Ekstra Ürünler (${selectedExtrasCount} seçili)` : `Optional Extras (${selectedExtrasCount} selected)`}
              </h3>
              <p className="text-[11px] text-gray-500 mb-3">
                {tr ? 'Paket dışında ek ürünler ekleyebilirsiniz.' : 'Add any additional products.'}
              </p>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={tr ? 'Ürün ara...' : 'Search products...'}
                  data-testid="extras-search-input"
                  className="w-full text-sm pl-8 pr-8 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
                  ><X className="h-3 w-3 text-gray-500" /></button>
                )}
              </div>

              {/* Category pills */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <button
                    onClick={() => { setCatFilter('ALL'); setSubCatFilter('ALL'); }}
                    className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors ${catFilter === 'ALL' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    data-testid="cat-filter-all"
                  >{tr ? 'Tümü' : 'All'}</button>
                  {categories.map(c => (
                    <button
                      key={c.name}
                      onClick={() => { setCatFilter(c.name); setSubCatFilter('ALL'); }}
                      className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors ${catFilter === c.name ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      data-testid={`cat-filter-${c.name}`}
                    >{c.name}</button>
                  ))}
                </div>
              )}

              {/* Sub-category pills for current category */}
              {catFilter !== 'ALL' && (() => {
                const catObj = categories.find(c => c.name === catFilter);
                if (!catObj || catObj.subs.length === 0) return null;
                return (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <button
                      onClick={() => setSubCatFilter('ALL')}
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${subCatFilter === 'ALL' ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' : 'bg-white text-gray-500 border border-gray-200'}`}
                    >{tr ? 'Tümü' : 'All'}</button>
                    {catObj.subs.map(s => (
                      <button
                        key={s}
                        onClick={() => setSubCatFilter(s)}
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${subCatFilter === s ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' : 'bg-white text-gray-500 border border-gray-200'}`}
                      >{s}</button>
                    ))}
                  </div>
                );
              })()}

              {/* Filtered list */}
              {filteredExtras.length === 0 ? (
                <p className="text-xs text-gray-400 italic py-4 text-center">
                  {tr ? 'Eşleşen ürün yok.' : 'No products match your filter.'}
                </p>
              ) : (
                <ul className="space-y-2 max-h-[30rem] overflow-y-auto pr-1">
                  {filteredExtras.map(ex => {
                  const exName = tr ? (ex.name_tr || ex.name) : (ex.name_en || ex.name);
                  const price = Number(ex.extra_price || 0);
                  const minQ = Number(ex.extra_min_qty || 1);
                  const maxQ = ex.extra_max_qty != null ? Number(ex.extra_max_qty) : null;
                  const trackStock = !!ex.track_stock;
                  const stockLeft = trackStock ? Number(ex.stock_qty || 0) : Number.MAX_SAFE_INTEGER;
                  const effMax = Math.min(maxQ ?? Number.MAX_SAFE_INTEGER, stockLeft);
                  const outOfStock = trackStock && stockLeft <= 0;
                  const q = Number(extraQtys[ex.id] || 0);
                  const included = Number(pkgQtys[ex.id] || 0);
                  const extraOnly = Math.max(0, q - included);
                  return (
                    <li
                      key={ex.id}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${included > 0 ? 'bg-indigo-50/60 border-indigo-200' : 'bg-gray-50 border-gray-100'}`}
                      data-testid={`extra-product-${ex.id}`}
                    >
                      {ex.thumb_url
                        ? <img src={ex.thumb_url} alt="" className="h-10 w-10 rounded object-cover" loading="lazy" />
                        : <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center"><Package className="h-5 w-5 text-gray-300" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-semibold text-gray-800 truncate">{exName}</p>
                          {included > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-black text-indigo-700 bg-white border border-indigo-200 px-1.5 py-0.5 rounded-full">
                              {tr ? `PAKETTE ${included}` : `IN PACKAGE ${included}`}
                            </span>
                          )}
                          {extraOnly > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-black text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                              +{extraOnly} {tr ? 'EKSTRA' : 'EXTRA'}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500">
                          {price > 0
                            ? `${price.toLocaleString('tr-TR')} ${ex.currency}/${ex.unit || 'adet'}`
                            : (tr ? 'Fiyat sorgulanacak' : 'Price on request')}
                          {trackStock && stockLeft < 999 && (
                            <span className={`ml-2 ${outOfStock ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                              · {outOfStock ? (tr ? 'Stok yok' : 'Out of stock') : (tr ? `Stok: ${stockLeft}` : `Stock: ${stockLeft}`)}
                            </span>
                          )}
                        </p>
                      </div>
                      {outOfStock ? (
                        <span className="text-[10px] font-bold text-red-600">—</span>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => bump(ex.id, -1)}
                            disabled={q <= included}
                            className="h-7 w-7 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center"
                            aria-label="minus"
                          ><Minus className="h-3.5 w-3.5" /></button>
                          <input
                            type="number"
                            min={included}
                            max={effMax}
                            value={q}
                            onChange={e => {
                              const v = Math.max(included, Math.min(effMax, Number(e.target.value) || 0));
                              setExtraQtys(st => ({ ...st, [ex.id]: v }));
                            }}
                            className="w-12 border border-gray-200 rounded px-1.5 py-0.5 text-xs text-center"
                            data-testid={`extra-qty-${ex.id}`}
                          />
                          <button
                            onClick={() => bump(ex.id, q === included && included === 0 ? minQ : 1, effMax)}
                            disabled={q >= effMax}
                            className="h-7 w-7 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700 disabled:opacity-40 flex items-center justify-center"
                            aria-label="plus"
                          ><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              )}
            </div>
          )}

          {!allocated && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2" data-testid="allocation-required-warning">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-900">
                  {tr ? 'Yer Ataması Gerekli' : 'Allocation Required'}
                </p>
                <p className="text-xs text-amber-700">
                  {tr
                    ? 'Bu paketi sepete eklemek için önce organizatörün size bir yer ataması yapması gerekiyor.'
                    : 'Before you can add this package to your cart, the organizer must assign you a location.'}
                </p>
              </div>
            </div>
          )}

          {/* Inline CTA — opens summary first */}
          <button
            onClick={() => setShowSummary(true)}
            disabled={adding || !stand.price || !allocated}
            data-testid="add-modular-stand-to-cart"
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3.5 rounded-xl text-sm shadow-lg shadow-indigo-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <ShoppingCart className="h-4 w-4" />
            {!allocated
              ? (tr ? 'Yer Ataması Bekleniyor' : 'Waiting for Allocation')
              : (selectedExtrasCount > 0
                  ? (tr ? `Sepet Özetini Gör (${selectedExtrasCount} ekstra)` : `Review Cart (${selectedExtrasCount} extras)`)
                  : (tr ? 'Sepet Özetini Gör' : 'Review Cart'))}
          </button>
        </div>
      </div>

      {/* Summary modal */}
      {showSummary && (
        <SummaryModal
          tr={tr}
          stand={stand}
          summaryLines={summaryLines}
          pkgItems={pkg?.items || []}
          extrasTotal={extrasTotal}
          grandTotal={grandTotal}
          adding={adding}
          onClose={() => setShowSummary(false)}
          onConfirm={handleAddToCart}
        />
      )}
    </div>
  );
}

/* ─── Summary Modal ──────────────────────────────────────────────────────── */
function SummaryModal({ tr, stand, summaryLines, pkgItems, extrasTotal, grandTotal, adding, onClose, onConfirm }) {
  const title = tr ? (stand.title_tr || stand.title) : (stand.title_en || stand.title);
  const cur   = stand.currency || 'EUR';
  const fmt   = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="cart-summary-modal"
      >
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">
              {tr ? 'Sepet Özeti' : 'Order Summary'}
            </p>
            <h3 className="font-black text-lg text-gray-900 leading-tight mt-0.5">{title}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Stand line */}
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 flex items-start gap-3">
            {stand.thumb_url && <img src={stand.thumb_url} alt="" className="h-12 w-12 rounded-lg object-cover" />}
            <div className="flex-1">
              <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider">
                {tr ? 'Modüler Stand (Paket)' : 'Modular Stand (Package)'}
              </p>
              <p className="font-bold text-gray-900 text-sm">{title}</p>
              {stand.area_m2 && <p className="text-xs text-gray-500">{stand.area_m2} m²</p>}
            </div>
            <p className="font-black text-indigo-900 text-sm whitespace-nowrap">{fmt(stand.price)} {cur}</p>
          </div>

          {/* Included items */}
          {pkgItems.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                {tr ? 'Pakete Dahil' : 'Included in Package'}
              </p>
              <ul className="space-y-1">
                {pkgItems.map((it, i) => {
                  const n = tr ? (it.product?.name_tr || it.product?.name_en) : (it.product?.name_en || it.product?.name_tr);
                  return (
                    <li key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-1.5">
                      <span className="text-gray-700 truncate">{n} × {it.qty}</span>
                      <span className="text-green-600 font-bold text-[10px]">{tr ? 'DAHİL' : 'INCLUDED'}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Extras */}
          {summaryLines.length > 0 ? (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                {tr ? `Ek Seçimler (${summaryLines.length})` : `Extras (${summaryLines.length})`}
              </p>
              <ul className="space-y-1.5">
                {summaryLines.map(ln => (
                  <li key={ln.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2" data-testid={`summary-extra-${ln.id}`}>
                    {ln.thumb && <img src={ln.thumb} alt="" className="h-9 w-9 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{ln.name}</p>
                      <p className="text-[11px] text-gray-500">
                        {ln.price > 0 ? `${fmt(ln.price)} ${ln.currency} × ${ln.qty}` : (tr ? `${ln.qty} adet — fiyat sorgulanacak` : `${ln.qty} units — price on request`)}
                      </p>
                    </div>
                    <p className="font-bold text-gray-900 text-sm whitespace-nowrap">
                      {ln.price > 0 ? `${fmt(ln.lineTotal)} ${ln.currency}` : '—'}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic text-center py-2">
              {tr ? 'Ekstra seçilmedi.' : 'No extras selected.'}
            </p>
          )}
        </div>

        {/* Totals + confirm */}
        <div className="border-t border-gray-100 p-5 bg-gray-50 space-y-2.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{tr ? 'Paket' : 'Package'}</span>
            <span className="font-semibold text-gray-900">{fmt(stand.price)} {cur}</span>
          </div>
          {extrasTotal > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{tr ? 'Ekstralar' : 'Extras'}</span>
              <span className="font-semibold text-gray-900">+{fmt(extrasTotal)} {cur}</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <span className="text-xs font-black uppercase tracking-wider text-gray-500">{tr ? 'Güncel Toplam' : 'Grand Total'}</span>
            <span className="font-black text-indigo-900 text-xl">{fmt(grandTotal)} {cur}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={onClose}
              disabled={adding}
              className="py-2.5 px-4 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-sm font-semibold text-gray-700 disabled:opacity-60"
              data-testid="summary-cancel-btn"
            >
              {tr ? 'Vazgeç' : 'Back'}
            </button>
            <button
              onClick={onConfirm}
              disabled={adding}
              className="py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-lg shadow-indigo-200 disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="summary-confirm-btn"
            >
              <ShoppingCart className="h-4 w-4" />
              {adding ? (tr ? 'Ekleniyor...' : 'Adding...') : (tr ? 'Sepete Ekle' : 'Add to Cart')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModularStandDetailBody;
